/* eslint-disable no-underscore-dangle */
import { noop } from '@ajaibid/utils';

const defaultIgnoreErrors = [/Message: Script error\./];

export const LOG_TYPE = {
  CONSOLE_ERROR: 'console.error',
  CONSOLE_WARN: 'console.warn',
  CONSOLE_LOG: 'console.log',
  CONSOLE_INFO: 'console.info',
  WINDOW_ERROR: 'window:onerror',
  WINDOW_UNHANDLE_REJECTION: 'window:unhandledrejection',
};

function flattenMessage(message) {
  // check whether the message is object and neither null nor undefined
  if (message && typeof message === 'object') {
    return Object.assign(
      {},
      ...(function _flatten(msg, prepend = '') {
        return [].concat(
          ...Object.keys(msg).map((o) => {
            const needFlatten =
              typeof msg[o] === 'object' && msg[o] !== null && msg[o] !== undefined;
            return needFlatten ? _flatten(msg[o], `${o}`) : { [`${prepend}${o}`]: msg[o] };
          })
        );
      })(message)
    );
  }
  return {};
}

function isHaveNR() {
  return window.newrelic && typeof window.newrelic.noticeError === 'function';
}

function _sendNrNotice(type, messages) {
  // newrelic doesn't automaticaly get the Error that not throwing Error stack
  // so we will try to send manually here to NR
  // only catch console.error for now
  // Read more on https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/noticeerror-browser-agent-api
  const isTypeError = type === LOG_TYPE.CONSOLE_ERROR || type === LOG_TYPE.WINDOW_ERROR;

  if (isHaveNR() && isTypeError) {
    // noticeError need to be send as a single error per request
    for (let index = 0; index < messages.length; index += 1) {
      const message = messages[index];

      // We will generate eventTracker object here

      if (typeof message === 'string') {
        window.newrelic.noticeError(message);
      } else {
        const title = message?.errorMessage || message?.message;
        // we don't need to send untitled error
        // it just bloated our NR without ability to understand the error
        if (title) {
          window.newrelic.noticeError(title, {
            ...flattenMessage(message),
          });
        }
      }
    }
  }
}

export function init(options) {
  const dummy = {
    enableEvent: noop,
    disableEvent: noop,
    setMetadata: noop,
    log: noop,
  };

  if (typeof window === 'undefined') {
    return dummy;
  }

  if (!options.enabled) {
    return dummy;
  }

  const _log = console.log;
  const _error = console.error;
  const _warning = console.warn;
  const _info = console.info;
  const ignoreErrors = options.ignoreErrors || defaultIgnoreErrors;
  const enable = options.enable || {
    log: false,
    info: false,
    error: true,
    warn: false,
  };
  const enableEventImmediately = options.enableEventImmediately ?? true;
  let clientLoggerEventEnabled = false;
  let metadata = {};

  const setMetadata = (data) => {
    metadata = {
      ...metadata,
      ...data,
    };
  };

  const postLog = (type, messages) => {
    const ignoreRegex = new RegExp(ignoreErrors.map((reg) => `(${reg.source})`).join('|'));
    const filteredMessages = messages.filter((m) => m && !ignoreRegex.test(m));
    if (!filteredMessages.length) {
      return;
    }

    // send noticeError to NR
    _sendNrNotice(type, filteredMessages);
  };

  const handleUnhandledRejection = (e) => {
    if (e.reason) {
      postLog(LOG_TYPE.WINDOW_UNHANDLE_REJECTION, [e.reason.stack || e.reason.message]);
    }
  };

  const handleOnError = (event) => {
    const { message, lineno, colno, error, type, filename } = event;

    postLog(LOG_TYPE.WINDOW_ERROR, [
      `Type: ${type};
       Filename: ${filename};
       Line: ${lineno};
       Col: ${colno};
       Message: ${message};
       Stack: ${error?.stack};`,
    ]);
  };

  const enableEvent = () => {
    if (clientLoggerEventEnabled) {
      return;
    }

    clientLoggerEventEnabled = true;
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleOnError);

    if (enable.error) {
      console.error = (...arg) => {
        postLog(LOG_TYPE.CONSOLE_ERROR, arg);
        _error.apply(console, arg);
      };
    }

    if (enable.warn) {
      console.warn = (...arg) => {
        postLog(LOG_TYPE.CONSOLE_WARN, arg);
        _warning.apply(console, arg);
      };
    }

    if (enable.log) {
      console.log = (...arg) => {
        postLog(LOG_TYPE.CONSOLE_LOG, arg);
        _log.apply(console, arg);
      };
    }

    if (enable.info) {
      console.info = (...arg) => {
        postLog(LOG_TYPE.CONSOLE_INFO, arg);
        _info.apply(console, arg);
      };
    }
  };

  const disableEvent = () => {
    clientLoggerEventEnabled = false;
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('error', handleOnError);

    if (enable.error) {
      console.error = _error;
    }
    if (enable.warn) {
      console.warn = _warning;
    }
    if (enable.log) {
      console.log = _log;
    }
    if (enable.info) {
      console.info = _info;
    }
  };

  if (enableEventImmediately) {
    enableEvent();
  }

  window._clientLogger = {
    enableEvent,
    disableEvent,
    setMetadata,
    log: postLog,
  };

  return window._clientLogger;
}

/**
 * Force send log to NewRelic
 * @param {string} message
 */
function sendNrNotice(error, additionalData) {
  if (!isHaveNR()) {
    console.warn('no newrelic detected');
    return;
  }

  window.newrelic.noticeError(error, additionalData);
}

export default {
  init,
  sendNrNotice,
};
